<template>
  <div class="doc-container font-roboto-light top-border">
    <div class="container">
      <div class="row flex-xl-nowrap g-0">
        <SidebarDocumentation/>
        <!-- ****************************** DOC MAIN BODY ********************************* -->
        <main class="col-xl-9 col-md-8 doc-main-body">
          <h2>Deploy the backend with GitHub Actions</h2>
          <p class="mt-15">GitHub Actions is a CI/CD solution from GitHub. GitHub Actions is a CI/CD solution from GitHub. When you push changes to your repository, it automatically updates your services. It is very popular and highly recommended. Follow these steps to set it up:</p>

          <h3>1. Set values for the global-dev-config.ts file</h3>
          <p>Now we need to configure the <span class="font-roboto-medium">global-dev-config.ts file</span>, which you'll find in the <span class="font-roboto-medium">'cdk/global-config'</span> folder.
            <br>This file represents the application configuration for your development environment that does not contain sensitive data (sensitive data is handled by AWS Secret Manager).
            <br> For the <span class="font-roboto-medium"> dev</span> account, change only these fields<span class="font-roboto-medium"></span>:
          </p>
          <CodeContainer :code="globalConfig.code" :language="globalConfig.language"/>
          <p>You can get the amplifyId by going to AWS Amplify and selecting your app.</p>
          <div class="documentation-image">
            <img
                src="../../../../assets/images/documentation/deployment/amplify_id.png"
                alt=""
                class="shapes screen-one"
            />
          </div>
          <h3>2. Set values for the global-prod-config.ts file</h3>
          <p>Similarly, we set up the <span class="font-roboto-medium">prod</span> account (<b>global-prod-config.ts file</b>). Change only the fields below <span class="font-roboto-medium"></span>. The difference is that you need to write to the domain that we configured when we <router-link to="/documentation/deploy-frontend" class="underline-link">deployed the frontend</router-link>. Also set the subdomain to empty, as we will not be using it. </p>
          <CodeContainer :code="globalConfig2.code" :language="globalConfig2.language"/>
          <h3>3. Push to the repository to save configs</h3>
          <p>Let's push to the repo to update your configs:</p>
          <CodeContainer :code="commitPushConfig.code" :language="commitPushConfig.language"/>

          <h3>4. Configure AWS Secret Manager</h3>
          <p>AWS Secret Manager is used to store sensitive data such as emails, API keys, etc.<br>
            We need to store some information so that GitHub Actions can use it when creating cloud resources.<br>
            We need to do this for both <span class="font-roboto-medium">dev</span> and <span class="font-roboto-medium">prod</span> accounts.
          </p>
          <p>Let's start with the <span class="font-roboto-medium">dev</span> account:</p>
          <ul class="list-item-one">
            <li>Go to AWS Secret Manager (Make sure the region is the same as the one you usually use), click <span class="font-roboto-medium">'Store new secret'</span>, select <span class="font-roboto-medium">'Other type of secret'</span> and store the following data:
              <div class="documentation-image">
                <img
                    src="../../../../assets/images/documentation/deployment/secrets_store_new_secret.png"
                    alt=""
                    class="shapes screen-one"
                />
              </div>
              <ul class="list-item-one" style="margin-left: 25px">
                <li class="mt-15">
                  contact_email_from, contact_email_to, test_user_1. These emails need to be <span class="font-roboto-medium">different</span>, so just use a '+' sign as below. For example, if you have an email address myemail@gmail.com, you can simply add a plus sign.
                  Result is myemail+from@gmail.com.
                  <InfoContainer class="mt-15">
                    <span class="font-roboto-medium">IMPORTANT</span>
                    If you're adding a '+' sign to make your email address unique, don't include other symbols like a '-' after it. For example, if your email is myemail@gmail.com, using myemail+from@gmail.com is fine, but myemail+from-me@gmail.com is not.
                  </InfoContainer>
                  <div class="documentation-image">
                    <img
                        src="../../../../assets/images/documentation/deployment/secrets_store_new_secret_github_actions.png"
                        alt=""
                        class="shapes screen-one"
                    />
                  </div>
                </li>
              </ul>

            </li>
            <li class="mt-15">
              Click <span class="font-roboto-medium">'Next'</span>, name your secret <span class="font-roboto-medium">saasconstruct/secrets</span> and finish the setup (just click 'Next' and 'Store'). Your secret will appear.
            </li>
            <li>
              Do the same for the <span class="font-roboto-medium">prod</span> account.
            </li>
            <li>
              Great! Let's move on to the next step!
            </li>
          </ul>


          <h3>5. Deploy the Trust Stack</h3>
          <p>
            We need to connect your GitHub repository and your AWS accounts so that GitHub Actions can provision resources in AWS. We will create a Trust Stack which will create AWS roles for your GitHub repository.<br>
          </p>
          <h4>Run npm install</h4>
          <CodeContainer :code="npmInstall.code" :language="npmInstall.language"/>
          <h4>Deploy the Trust Stack to the dev account</h4>
          <p>First, we will deploy to the <span class="font-roboto-medium">dev</span> account, and then repeat the same steps for the <span class="font-roboto-medium">prod</span> account.</p>
          <ul class="list-item-one">
            <li>
              Go to your Access Portal and copy the commands to set the AWS credentials to your local machine. These are temporary credentials that will only be used for the initial setup.
              <div class="documentation-image">
                <img
                    src="../../../../assets/images/documentation/deployment/access_portal_temp_creds.png"
                    alt=""
                    class="shapes screen-one"
                />
              </div>
              <div class="documentation-image">
                <img
                    src="../../../../assets/images/documentation/deployment/access_portal_temp_creds_2.png"
                    alt=""
                    class="shapes screen-one"
                />
              </div>
            </li>
            <li class="mt-15">
              Run the copied commands in your terminal. Example for MacOS and Linux:
            </li>
            <CodeContainer :code="exportAwsKey.code" :language="exportAwsKey.language"/>
            <p>Note: You have set up temporary credentials for the dev account. To check if this worked, you can run any AWS CLI command that returns the account id. Example:</p>
            <CodeContainer :code="checkAccount.code" :language="checkAccount.language"/>
            <li class="mt-15">
              Run the following commands. This will bootstrap your <span class="font-roboto-medium">dev</span> account (it will set up CDK in your dev account).
            </li>
            <CodeContainer :code="bootstrapDev.code" :language="bootstrapDev.language"/>
            <li class="mt-15">
              Run the following commands to check if the template is ready for deployment. This will output several stacks, including: <span class="font-roboto-medium">SaasConstruct-GitHubActionsTrustStack-dev, SaasConstruct-GitHubActionsTrustStack-prod</span>.
            </li>
            <CodeContainer :code="cdkDeploy.code" :language="cdkDeploy.language"/>
            <li class="mt-15">
              Run the following command for the <span class="font-roboto-medium">dev</span> account:
            </li>
            <CodeContainer :code="cdkDeploy2.code" :language="cdkDeploy2.language"/>
            <p>Great, now let's do this again for <span class="font-roboto-medium">prod</span> account</p>
          </ul>

          <h4>Deploy to the prod account</h4>
          <p>The steps for the prod account are the same as for the dev account.</p>
          <ul class="list-item-one">
            <li>
              <span class="font-roboto-medium">Go to your Access Portal and copy the commands to set the AWS credentials </span>. Now choose the <span class="font-roboto-medium">prod</span> account.
            </li>
            <li>
              Run the copied commands in your terminal.
            </li>
            <li>
              Bootstrap the prod account.
            </li>
            <CodeContainer :code="bootstrapProd.code" :language="bootstrapProd.language"/>
            <li>
              Run the following command for the <span class="font-roboto-medium">prod</span> account:
            </li>
            <CodeContainer :code="cdkDeploy3.code" :language="cdkDeploy3.language"/>
          </ul>
          <p class="mt-15"><span class="font-roboto-medium">Congratulations!</span>  You can now see these stacks in Cloudformation in your <span class="font-roboto-medium">dev and prod </span> AWS accounts.
            <br>Example:
          </p>
          <div class="documentation-image">
            <img
                src="../../../../assets/images/documentation/deployment/trust_stack.png"
                alt=""
                class="shapes screen-one"
            />
          </div>
          <p class="mt-25">Now let's set up GitHub Actions and deploy the backend.</p>

          <h3>6. Set variables in GitHub actions</h3>
          <ul class="list-item-one">
            <li>
              Go to AWS Cloudformation and copy <span class="font-roboto-medium">the ARN of the AWS role for GitHub actions</span> (do this for both dev and prod accounts).
            </li>
            <div class="documentation-image">
              <img
                  src="../../../../assets/images/documentation/deployment/cf.png"
                  alt=""
                  class="shapes screen-one"
              />
            </div>
            <div class="documentation-image mt-25">
              <img
                  src="../../../../assets/images/documentation/deployment/cf2.png"
                  alt=""
                  class="shapes screen-one"
              />
            </div>
            <div class="documentation-image mt-25">
              <img
                  src="../../../../assets/images/documentation/deployment/cf3.png"
                  alt=""
                  class="shapes screen-one"
              />
            </div>
            <li class="mt-25">
              Now go to your GitHub repository settings and create the following variables:
              <ul class="list-item-one mt-10">
                <li>
                  AWS_DEV_ACCOUNT_ID - your dev account id.
                </li>
                <li>
                  AWS_DEV_ROLE - ARN of the role you copied in the previous step (for the dev account).
                </li>
                <li>
                  AWS_MAIN_ACCOUNT_ID - your prod account id.
                </li>
                <li>
                  AWS_MAIN_ROLE - ARN of the role you copied in the previous step (for the prod account).
                </li>
                <li>
                  AWS_TARGET_REGION - your AWS region.
                </li>
                <li>
                  CDK_STACK_DEV - SaasConstruct-AppStack-dev
                </li>
                <li>
                  CDK_STACK_PROD - SaasConstruct-AppStack-prod
                </li>


              </ul>
              <div class="documentation-image mt-25">
                <img
                    src="../../../../assets/images/documentation/deployment/github_actions_vars.png"
                    alt=""
                    class="shapes screen-one"
                />
              </div>
              <div class="documentation-image mt-25">
                <img
                    src="../../../../assets/images/documentation/deployment/github_actions_vars2.png"
                    alt=""
                    class="shapes screen-one"
                />
              </div>
            </li>

          </ul>

          <h3 class="mt-25">7. Deploy the backend</h3>
          <p>Now all we need to do is to push the code to your GitHub repository.</p>

          <ul class="list-item-one">

            <InfoContainer class="mt-15">
              <span class="font-roboto-medium">IMPORTANT</span>
              After you've pushed the code, and while GitHub Actions is doing a rollout, you'll receive emails (check the spam folder too) from AWS to your inbox (the ones you entered in Secret Manager), asking you to confirm that you want to receive notifications from AWS (e.g. if traffic to your site spikes unexpectedly, etc). Please confirm them all.
            </InfoContainer>
            <CodeContainer class="mt-15" :code="commitPushFinal.code" :language="commitPushFinal.language"/>
            <li class="mt-15">
              You can check your pipeline at any time in the GitHub Actions tab of your GitHub repository.
              <div class="documentation-image">
                <img
                    src="../../../../assets/images/documentation/deployment/github_actions_pipeline.png"
                    alt=""
                    class="shapes screen-one"
                />
              </div>
            </li>
            <li class="mt-15">After the rollout has been completed, the last step is to make sure your newly created resources are synchronised with the frontend. <br>
              To do this, just do an empty git commit and push the code.
            </li>
            <CodeContainer class="mt-15" :code="commitPushFinalSync.code" :language="commitPushFinalSync.language"/>
            <p>Alternatively, you can go to AWS Amplify (for both  <span class="font-roboto-medium">dev</span> and  <span class="font-roboto-medium">prod</span>) and simply click:  <span class="font-roboto-medium">Redeploy this version.</span></p>
            <div class="documentation-image">
              <img
                  src="../../../../assets/images/documentation/deployment/amplify_redeploy.png"
                  alt=""
                  class="shapes screen-one"
              />
            </div>
          </ul>
          <p><span class="font-roboto-medium">Congratulations!</span>.</p>

          <h3 style="margin-top: 30px !important;">7. Using the website</h3>


          <ul class="list-item-one">
            <p>You are now ready to use the website (both dev and prod versions):</p>
            <div class="documentation-image">
              <img
                  src="../../../../assets/images/documentation/deployment/initial_run.gif"
                  alt=""
                  class="shapes screen-one"
              />
            </div>

            <li class="mt-15">
              <span class="font-roboto-medium">Sign up with email and password</span>.
              <InfoContainer class="mt-15 mb-10">
                <span class="font-roboto-medium">INFO</span>
                Currently you can use a test user email you've provided to the Secret Manager.
                The reason is that AWS does not give you full access to AWS email service (AWS SES) from the start. <br>
                You will have to request AWS (prod account only) to give you production access to AWS SES. <router-link to="/documentation/request-ses" class="underline-link">We will do it later.</router-link> <br>
                If you want to use additional emails now, just add a new verified identity (email) in AWS SES (AWS SES -> Identities -> Create identity).
              </InfoContainer>
            </li>
            <li>
              <span class="font-roboto-medium">Sign in with email and password and sign out</span>. We will configure Google signup/signin later.
            </li>
            <li>
              <span class="font-roboto-medium">Change email or password, change language. Delete a user</span>.
            </li>
            <li>
              <span class="font-roboto-medium">Add and delete items in the /service page</span>.
            </li>
            <li>
              <span class="font-roboto-medium">Use the contact form to send emails about a product</span>.
            </li>
            <li>The rest of the functionality, such as  <span class="font-roboto-medium">payments and the chatbot</span>, <span class="font-roboto-medium">will be configured later. Just follow the documentation</span>.</li>
          </ul>




          <DocumentationPagination/>
        </main> <!-- /.doc-main-body -->
      </div>
    </div>

  </div>
</template>

<script>
import SidebarDocumentation from "@/components/service-documentation/commons/SidebarDocumentation.vue";
import DocumentationPagination from "@/components/service-documentation/commons/DocumentationPagination.vue";
import CodeContainer from "@/components/service-documentation/commons/CodeContainer.vue";
import {ref} from "vue";
import InfoContainer from "@/components/service-documentation/commons/InfoContainer.vue";

export default {
  name: 'DeployBackendGitHubActions',
  components: {
    InfoContainer,
    CodeContainer,
    SidebarDocumentation,
    DocumentationPagination,
  },
  setup() {

    const globalConfig = ref({
      language: 'typescript',
      code:
          'cdkPipelines: {\n' +
          '    isUsingCdkPipeline: false,\n' +
          '    // ignore the rest in cdkPipelines \n' +
          '},\n' +
          'googleSignInSupported: false, // Google Sign-In support (enabled only after setting up credentials in Google Cloud Console)\n' +
          '\n' +
          '// Web page hosting\n' +
          'amplifyId: "YOUR AMPLIFY APP ID", // You can get the amplifyId by going to AWS Amplify and selecting your app.\n' +
          '\n' +
          '// Repository and configuration\n' +
          'repoString: "YOUR GITHUB USERNAME/saasconstruct", // <github_name>/<repository_name>\n' +
          'appRandomNameExtension: "RANDOM NUMBERS HERE", // Put random numbers here like 18234 (used for uniqueness, can\'t be very long).\n' +
          '// Domain configuration\n' +
          'domain: "", // leave it empty\n' +
          'subdomain: "", // leave it empty'
    });

    const globalConfig2 = ref({
      language: 'typescript',
      code:
          'cdkPipelines: {\n' +
          '    isUsingCdkPipeline: false,\n' +
          '    // ignore the rest in cdkPipelines \n' +
          '},\n' +
          'googleSignInSupported: false, // Google Sign-In support (enabled only after setting up credentials in Google Cloud Console)\n' +
          '\n' +
          '// Web page hosting\n' +
          'amplifyId: "YOUR AMPLIFY APP ID", // You can get the amplifyId by going to AWS Amplify and selecting your app.\n' +
          '\n' +
          '// Repository and configuration\n' +
          'repoString: "YOUR GITHUB USERNAME/saasconstruct", // <github_name>/<repository_name>\n' +
          'appRandomNameExtension: "RANDOM NUMBERS HERE", // Put random numbers here like 18234 (used for uniqueness, can\'t be very long).\n' +
          '// Domain configuration\n' +
          'domain: "YOUR DOMAIN", // your domain name\n' +
          'subdomain: "", // leave it empty'
    });

    const commitPushConfig= ref({
      language: 'bash',
      code:
          'cd saasconstruct # navigate to the project folder\n' +
          'git add .\n'+
          'git commit -m "set configs"\n'+
          'git push origin main # pushes code to the main branch (prod)\n'+
          'git push origin main:development # pushes current main branch to development (dev)\n'
    });

    const npmInstall = ref({
      language: 'bash',
      code:
          'cd cdk\n' +
          'npm install'
    });

    const bootstrapDev = ref({
      language: 'bash',
      code:
          '# <dev-account-id> is your dev account id (you can find it in the Access Portal)\n' +
          '# <region> is your chosen region\n' +
          'cdk bootstrap aws://<dev-account-id>/<region> --cloudformation-execution-policies arn:aws:iam::aws:policy/AdministratorAccess'
    });
    const bootstrapProd = ref({
      language: 'bash',
      code:
          '# <prod-account-id> is your prod account id (you can find it in the Access Portal)\n' +
          '# <region> is your chosen region\n' +
          'cdk bootstrap aws://<prod-account-id>/<region> --cloudformation-execution-policies arn:aws:iam::aws:policy/AdministratorAccess'
    });

    const exportAwsKey = ref({
      language: 'bash',
      code:
          "export AWS_ACCESS_KEY_ID=\"ASIA27P6XSBHH7MXL\"\n" +
          "export AWS_SECRET_ACCESS_KEY=\"P3I1KcCO/ENtQX8Lomx7XhkKgPPVZHK96CSWi\"\n" +
          "export AWS_SESSION_TOKEN=\"IQoJb3JpZ2luX2VjEOX//////////wEaDGV1LWNlbnRyYWwtMSJIMEYCIQCD5CRONW6crS4Jp3hMf66cLf1ImrjK4j67i2TekQgPnAIhANbH6uqkpIpeH1pXOiBm1c7+zOQvTdnVkEKY2THh34adKu8CCL///////////wEQARoMNzU0ODM3ODUyMjM5Igxp+ViwKEB+3rTdvTYqwwKrrpvsD9ZrxfGh46ysHydDggT7iDPRhTp5qx9X8i17CEe7VhrN3gYJmcS6SUk6DcNJR+kqra+D5czQn7IjnNvV5aj9VY0lQs/Yz+kJcQlurZgIjUM1Jit72Ur0uk7VKyqPQ37mUXqmsCRTPeULc8fnB/Jn+x2SM1tRoAE3NhpBqWXq5fHYsYk196Xma1QS0anyuYFzccJtbfgrWsSqEcmv3POA5zw7yrSffcIj2jRuwdYeGw+rUYIcS3xWPCTfz8Rz1Jj+fQlzL3kIRQ/oLQUElySUoTCT8hKxDIRDV+WgqMaG0ikbKldj4lS3Oihx8+pepZEUkbjm05gMgr3jOspatZmLr1o5j1MfbWBEJ0SaURl2+4qUwv4vcbldJBkMWIVxZtJQs8SbeK1GJF73eFQ1LbvDpa4OQXwn6in70R2pu56ZGTDKo4m1BjqmAavkxgL/h7BWXFb60i0p4RvBqUhH9yV/cdea0YLaP9uRQGQb6QRSmaCF7+s/vAN7+PLo4B7D1G1pevrU+JlIcpGOZNlj5Fyx/wcfU1DZHM4ZkJwX23UqmF+0TB4Z8NRcRyZK+PTu4Ht4URDZfM5fyfOyrQ37iiAFnV+nIjEbvYmp8QSKNTvGzG7rmcgfk=\""
    });
    const cdkDeploy = ref({
      language: 'bash',
      code:
          '# from the root folder of the project (folder /saasconstruct)\n' +
          'cd cdk # navigate to the infrastructure folder\n' +
          "cdk ls\n"
    });
    const cdkDeploy2 = ref({
      language: 'bash',
      code:
          '# from the cdk folder of the project (folder /saasconstruct/cdk)\n' +
          'AWS_REGION=<YOUR AWS REGION> cdk deploy SaasConstruct-GitHubActionsTrustStack-dev --parameters GitHubUsername=<YOUR GITHUB USERNAME> --parameters GitHubRepo=<YOUR GITHUB REPO>\n'
    });
    const cdkDeploy3 = ref({
      language: 'bash',
      code:
          '# from the cdk folder of the project (folder /saasconstruct/cdk)\n' +
          'AWS_REGION=<YOUR AWS REGION> cdk deploy SaasConstruct-GitHubActionsTrustStack-prod --parameters GitHubUsername=<YOUR GITHUB USERNAME> --parameters GitHubRepo=<YOUR GITHUB REPO>\n'
    });
    const checkAccount = ref({
      language: 'bash',
      code:
          '# compare the output of this command with your account id\n' +
          'aws sts get-caller-identity --query Account --output text'
    });
    const commitPushFinal = ref({
      language: 'bash',
      code:
          'cd saasconstruct # navigate to the project folder\n' +
          'git add .\n'+
          'git commit -m "deploy backend"\n'+
          'git push origin main # pushes code to the main branch (prod)\n'+
          'git push origin main:development # pushes current main branch to development (dev)\n'

    });
    const commitPushFinalSync = ref({
      language: 'bash',
      code:
          'cd saasconstruct # navigate to the project folder\n' +
          'git add .\n'+
          'git commit --allow-empty -m "empty"\n'+
          'git push origin main # pushes code to the main branch (prod)\n'+
          'git push origin main:development # pushes current main branch to development (dev)\n'

    });


    return {
      globalConfig,
      globalConfig2,
      commitPushConfig,
      bootstrapDev,
      bootstrapProd,
      exportAwsKey,
      cdkDeploy,
      cdkDeploy2,
      cdkDeploy3,
      checkAccount,
      commitPushFinal,
      commitPushFinalSync,
      npmInstall
    }
  }
}
</script>



